import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  ShieldAlert, Activity, SlidersHorizontal, ScrollText, Eye, KeyRound, DatabaseBackup,
  Copy, Ban, Plus, RefreshCw,
} from 'lucide-react';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { WidgetLoading } from '@/features/dashboard/infrastructure/widgets/WidgetStates';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { cn } from '@/shared/utils/utils';
import { useUser } from '@/shared/hooks/useAuth';
import { isExecutive, identityOf } from '@/shared/permissions/identity';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useSystemHealth, useOrgSettings, useUpsertOrgSetting, useAuditLog,
  useApiKeys, useCreateApiKey, useRevokeApiKey, useBackups, useRunBackup, useOversight,
} from '../hooks/useCommandCenter';
import { resolveGateState } from '../gateState';
import type { OversightDomain } from '../types';

type TabKey = 'health' | 'org' | 'audit' | 'oversight' | 'keys' | 'backups';
const TABS: { key: TabKey; label: string; icon: typeof Activity }[] = [
  { key: 'health', label: 'System Health', icon: Activity },
  { key: 'org', label: 'Org Settings', icon: SlidersHorizontal },
  { key: 'audit', label: 'Audit Center', icon: ScrollText },
  { key: 'oversight', label: 'Oversight', icon: Eye },
  { key: 'keys', label: 'API Keys', icon: KeyRound },
  { key: 'backups', label: 'Backups', icon: DatabaseBackup },
];

/** Mandatory-state gate (loading / error+retry / empty / content / not-enabled)
 *  — Req 14.1–14.4. State selection is delegated to the pure `resolveGateState`
 *  resolver so the UI and the four-state test agree exactly. */
function Gate<T>({ q, empty, enabled = true, children }: {
  q: { isLoading: boolean; isError: boolean; error?: unknown; data?: T; refetch: () => void };
  empty: { title: string; description?: string };
  enabled?: boolean;
  children: (data: T) => React.ReactNode;
}) {
  const state = resolveGateState(q, enabled);
  if (state === 'not-enabled') {
    return <EmptyState title="Not enabled yet" description="This capability is additive and not yet shipped." />;
  }
  if (state === 'loading') {
    return <div className="py-4"><WidgetLoading variant="table" /></div>
  }
  if (state === 'error') {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <p className="text-body-sm text-danger-text">{getApiErrorMessage(q.error, 'Failed to load.')}</p>
        <Button variant="outline" size="sm" onClick={() => q.refetch()}><RefreshCw className="h-4 w-4" /> Retry</Button>
      </div>
    );
  }
  if (state === 'empty') {
    return <EmptyState title={empty.title} description={empty.description} />;
  }
  return <>{children(q.data as T)}</>;
}

// ── Health ───────────────────────────────────────────────────────────────────
function HealthTab() {
  const q = useSystemHealth();
  return (
    <Gate q={q} empty={{ title: 'No health data' }}>
      {(h) => (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            ['Uptime', `${Math.floor(h.uptimeSec / 60)}m ${h.uptimeSec % 60}s`],
            ['MongoDB', h.mongo],
            ['RSS', `${h.memory.rssMB} MB`],
            ['Heap used', `${h.memory.heapUsedMB} MB`],
            ['Node', h.node],
            ['As of', new Date(h.timestamp).toLocaleTimeString()],
          ].map(([k, v]) => (
            <Card key={k}><CardBody><p className="text-body-sm text-on-surface-variant">{k}</p><p className="text-data-lg font-semibold text-on-surface">{v}</p></CardBody></Card>
          ))}
        </div>
      )}
    </Gate>
  );
}

// ── Org settings ──────────────────────────────────────────────────────────────
function OrgTab() {
  const q = useOrgSettings();
  const upsert = useUpsertOrgSetting();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const save = () => {
    if (!key.trim()) return toast.error('Enter a setting key.');
    upsert.mutate({ key: key.trim(), value }, {
      onSuccess: () => { toast.success(`Saved "${key.trim()}".`); setKey(''); setValue(''); },
      onError: (e) => toast.error(getApiErrorMessage(e, 'Save failed.')),
    });
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <Input name="org-key" label="Key" value={key} onChange={(e) => setKey(e.target.value)} placeholder="e.g. theme" />
        <Input name="org-value" label="Value" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. dark" />
        <Button onClick={save} loading={upsert.isPending}><Plus className="h-4 w-4" /> Save setting</Button>
      </div>
      <Gate q={q} empty={{ title: 'No org settings yet', description: 'Create the first setting above.' }}>
        {(rows) => (
          <div className="divide-y divide-outline rounded-card border border-outline">
            {rows.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-4 py-2">
                <span className="font-medium text-on-surface">{s.key}</span>
                <span className="text-data-md tabular-nums text-on-surface-variant">{JSON.stringify(s.value)}</span>
              </div>
            ))}
          </div>
        )}
      </Gate>
    </div>
  );
}

// ── Audit center ───────────────────────────────────────────────────────────────
function AuditTab() {
  const [action, setAction] = useState<string>('');
  const q = useAuditLog(action || undefined);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1">
        {['', 'oversight_access', 'key_created', 'backup_run', 'security_event', 'org_setting_updated'].map((a) => (
          <button key={a || 'all'} type="button" onClick={() => setAction(a)}
            className={cn('rounded-full border px-3 py-1 text-body-sm', action === a ? 'border-primary text-primary' : 'border-outline text-on-surface-variant')}>
            {a || 'all'}
          </button>
        ))}
      </div>
      <Gate q={q} empty={{ title: 'No audit entries' }}>
        {(rows) => (
          <div className="max-h-[28rem] space-y-1 overflow-y-auto">
            {rows.map((e) => (
              <div key={e.id} className="rounded-card border border-outline px-3 py-2">
                <div className="flex items-center gap-2">
                  <Badge tone="info">{e.action}</Badge>
                  <span className="text-body-sm text-on-surface-variant">{e.display_name ?? '—'}</span>
                  <span className="ml-auto text-body-sm text-on-surface-variant">{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</span>
                </div>
                <p className="mt-1 text-body-sm text-on-surface">{e.detail}</p>
              </div>
            ))}
          </div>
        )}
      </Gate>
    </div>
  );
}

// ── Oversight console (justification → results) ─────────────────────────────────
function OversightTab() {
  const [domain, setDomain] = useState<OversightDomain>('employees');
  const [justification, setJustification] = useState('');
  const oversight = useOversight();
  const run = () => {
    if (!justification.trim()) return toast.error('A justification is required for oversight access.');
    oversight.mutate({ domain, justification: justification.trim() }, {
      onError: (e) => toast.error(getApiErrorMessage(e, 'Oversight failed.')),
    });
  };
  const result = oversight.data;
  return (
    <div className="space-y-4">
      <div className="rounded-card border border-warning/40 bg-warning/5 p-3 text-body-sm text-on-surface-variant">
        Oversight is overt and governed: access requires a written justification and is itself recorded in the Audit Center.
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-body-sm">
          Domain
          <select value={domain} onChange={(e) => setDomain(e.target.value as OversightDomain)}
            className="rounded-card border border-outline bg-surface px-3 py-2 text-on-surface">
            <option value="employees">Employees</option>
            <option value="leads">Leads</option>
            <option value="deals">Deals</option>
            <option value="attendance">Attendance</option>
          </select>
        </label>
        <Input name="justification" label="Justification (required)" value={justification} onChange={(e) => setJustification(e.target.value)} placeholder="Reason for this access" />
        <Button onClick={run} loading={oversight.isPending}><Eye className="h-4 w-4" /> Access</Button>
      </div>
      {result && (
        <div className="rounded-card border border-outline p-3">
          <p className="text-body-sm text-on-surface-variant">{result.results.length} {result.domain} record(s) · justification recorded to audit.</p>
        </div>
      )}
    </div>
  );
}

// ── API keys ───────────────────────────────────────────────────────────────────
function KeysTab() {
  const q = useApiKeys();
  const create = useCreateApiKey();
  const revoke = useRevokeApiKey();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [rawKey, setRawKey] = useState<string | null>(null);
  const doCreate = () => {
    if (!label.trim()) return toast.error('Enter a key label.');
    create.mutate({ label: label.trim(), scopes: ['read'] }, {
      onSuccess: (k) => { setRawKey(k.key); setLabel(''); },
      onError: (e) => toast.error(getApiErrorMessage(e, 'Create failed.')),
    });
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => { setRawKey(null); setOpen(true); }}><Plus className="h-4 w-4" /> New key</Button>
      </div>
      <Gate q={q} empty={{ title: 'No API keys', description: 'Create one to integrate external systems.' }}>
        {(keys) => (
          <div className="divide-y divide-outline rounded-card border border-outline">
            {keys.map((k) => (
              <div key={k.id} className="flex items-center gap-2 px-4 py-2">
                <span className="font-medium text-on-surface">{k.label}</span>
                {k.revoked_at ? <Badge tone="danger">revoked</Badge> : <Badge tone="success">active</Badge>}
                <span className="ml-auto text-body-sm text-on-surface-variant">{k.created_at ? new Date(k.created_at).toLocaleDateString() : ''}</span>
                {!k.revoked_at && (
                  <button type="button" aria-label="Revoke key" onClick={() => revoke.mutate(k.id, { onSuccess: () => toast.success('Key revoked.') })}
                    className="rounded-md p-1.5 text-on-surface-variant hover:bg-danger/10 hover:text-danger-text"><Ban className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </Gate>

      <Modal open={open} onClose={() => setOpen(false)} title="Create API key" size="sm"
        footer={rawKey ? <Button onClick={() => setOpen(false)}>Done</Button> : (
          <><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={doCreate} loading={create.isPending}>Create</Button></>
        )}>
        {rawKey ? (
          <div className="space-y-2">
            <p className="text-body-sm text-on-surface-variant">Copy this key now — it is shown only once and never again.</p>
            <div className="flex items-center gap-2 rounded-card border border-outline bg-surface-variant px-3 py-2">
              <code className="flex-1 break-all text-body-sm text-on-surface">{rawKey}</code>
              <button type="button" aria-label="Copy" onClick={() => { navigator.clipboard?.writeText(rawKey); toast.success('Copied.'); }}
                className="rounded-md p-1.5 text-on-surface-variant hover:bg-surface"><Copy className="h-4 w-4" /></button>
            </div>
          </div>
        ) : (
          <Input name="key-label" label="Label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Zapier integration" autoFocus />
        )}
      </Modal>
    </div>
  );
}

// ── Backups ────────────────────────────────────────────────────────────────────
function BackupsTab() {
  const q = useBackups();
  const run = useRunBackup();
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-card border border-danger/40 bg-danger/5 p-3">
        <p className="text-body-sm text-on-surface-variant">Recording a backup run is a high-risk operation and is audit-logged.</p>
        <Button size="sm" onClick={() => run.mutate('full', { onSuccess: () => toast.success('Backup recorded.'), onError: (e) => toast.error(getApiErrorMessage(e, 'Backup failed.')) })} loading={run.isPending}>
          <DatabaseBackup className="h-4 w-4" /> Record full backup
        </Button>
      </div>
      <Gate q={q} empty={{ title: 'No backups recorded yet' }}>
        {(rows) => (
          <div className="divide-y divide-outline rounded-card border border-outline">
            {rows.map((b) => (
              <div key={b.id} className="flex items-center gap-2 px-4 py-2">
                <span className="font-medium text-on-surface">{b.kind}</span>
                <Badge tone="info">{b.status}</Badge>
                <span className="ml-auto text-body-sm text-on-surface-variant">{b.created_at ? new Date(b.created_at).toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        )}
      </Gate>
    </div>
  );
}

/**
 * CEO Command Center — the hidden executive governance surface. Access is gated
 * to the Executive tier at three layers: the sidebar entry is Executive-only,
 * the route is wrapped in a superadmin RoleRoute, and every backend endpoint is
 * requireSuperAdmin (identical 403 otherwise). This component adds a final
 * client-side stealth guard so a non-executive who somehow reaches it sees
 * nothing privileged.
 */
export function CommandCenterPage() {
  const user = useUser();
  const [tab, setTab] = useState<TabKey>('health');

  if (!isExecutive(user?.role)) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState title="Not available" description="This area is not available for your account." />
      </div>
    );
  }

  const identity = identityOf({ role: user?.role, displayName: user?.displayName });

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-16">
      <PageHeader
        icon={ShieldAlert}
        eyebrow="Insights"
        title="CEO Command Center"
        description={<>{identity.attribution} · executive governance, oversight and system controls.</>}
      />

      <div className="flex flex-wrap gap-1 border-b border-outline">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={cn('flex items-center gap-2 border-b-2 px-4 py-2 text-data-md font-medium transition-colors',
                active ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant hover:text-on-surface')}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex-1">
        {tab === 'health' && <HealthTab />}
        {tab === 'org' && <OrgTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'oversight' && <OversightTab />}
        {tab === 'keys' && <KeysTab />}
        {tab === 'backups' && <BackupsTab />}
      </div>
    </div>
  );
}
